import React, { useMemo, useState } from "react";
import {
  Layout,
  Button,
  Space,
  Typography,
  Drawer,
  Form,
  Input,
  Avatar,
  App as AntApp,
  Switch,
  Tooltip,
} from "antd";
import { LogoutOutlined, SaveOutlined, MoonFilled, SunFilled } from "@ant-design/icons";
import { useAuth } from "../auth/AuthContext.tsx";
import { accountApi } from "../auth/apiClient.ts";
import { useThemeMode } from "../routes/ThemeProvider.tsx";

const { Header } = Layout;
const { Text } = Typography;

type PasswordForm = {
  currentPassword: string;
  newPassword: string;
};

export default function AppHeader(): JSX.Element {
  const { message } = AntApp.useApp();
  const { user, isAuthenticated, logout } = useAuth();
  const { isDark, toggle } = useThemeMode();

  const [open, setOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [form] = Form.useForm<PasswordForm>();

  const avatarSeed = useMemo<number>(() => new Date().getDate(), []);

  const onOpen = () => setOpen(true);
  const onClose = () => {
    form.resetFields();
    setOpen(false);
  };

  const showFieldError = (name: keyof PasswordForm, errMsg: string) => {
    form.setFields([{ name, errors: [errMsg] }]);
  };

  const onSave = async (): Promise<void> => {
    try {
      const { currentPassword, newPassword } = await form.validateFields();
      setSubmitting(true);

      const result = await accountApi.changePassword({ currentPassword, newPassword });

      if (result.ok) {
        message.success("Пароль обновлён");
        onClose();
        return;
      }

      const msg = result.message || "Не удалось обновить пароль";
      if (result.status === 400) {
        if (/текущ/i.test(msg)) {
          showFieldError("currentPassword", msg);
        } else if (/нов/iu.test(msg) || /короче/iu.test(msg) || /совпада/iu.test(msg)) {
          showFieldError("newPassword", msg);
        }
      }
      message.error(msg);
    } catch (err: any) {
      message.error(err?.message || "Ошибка отправки запроса");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Header
      style={{
        background: "var(--wo-header-bg)",
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        paddingInline: 16,
        borderBottom: "1px solid var(--wo-border)",
        height: 64,
      }}
    >
      <Text style={{ color: "var(--wo-text)", fontWeight: 600, fontSize: 18 }}>Jetrub</Text>

      <Space align="center" size={12}>
        <Tooltip title={isDark ? "Светлая тема" : "Тёмная тема"}>
          <Switch
            checked={isDark}
            onChange={toggle}
            checkedChildren={<MoonFilled />}
            unCheckedChildren={<SunFilled />}
          />
        </Tooltip>

        {isAuthenticated && (
          <>
            <Button
              type="text"
              onClick={onOpen}
              icon={
                <Avatar
                  size={30}
                  src={`https://api.dicebear.com/7.x/miniavs/svg?seed=${avatarSeed}`}
                  alt="avatar"
                  style={{ verticalAlign: "middle" }}
                />
              }
            >
              {user?.name} ({user?.role})
            </Button>
            <Button type="link" icon={<LogoutOutlined />} onClick={logout}>
              Выход
            </Button>
          </>
        )}
      </Space>

      <Drawer
        title="Смена пароля"
        open={open}
        onClose={onClose}
        width={420}
        mask={false}
        destroyOnClose
        extra={
          <Space>
            <Button onClick={onClose}>Отмена</Button>
            <Button type="primary" icon={<SaveOutlined />} loading={submitting} onClick={onSave}>
              Сохранить
            </Button>
          </Space>
        }
        styles={{ body: { paddingBottom: 16 } }}
      >
        <Form<PasswordForm> form={form} layout="vertical" requiredMark={false} onFinish={onSave}>
          <Form.Item
            name="currentPassword"
            label="Текущий пароль"
            rules={[
              { required: true, message: "Введите текущий пароль" },
              { min: 6, message: "Минимум 6 символов" },
            ]}
          >
            <Input.Password
              placeholder="Ваш текущий пароль"
              autoComplete="current-password"
              onPressEnter={() => onSave()}
            />
          </Form.Item>

          <Form.Item
            name="newPassword"
            label="Новый пароль"
            rules={[
              { required: true, message: "Введите новый пароль" },
              { min: 8, message: "Минимум 8 символов" },
              ({ getFieldValue }) => ({
                validator(_, value) {
                  if (!value || value !== getFieldValue("currentPassword")) return Promise.resolve();
                  return Promise.reject(new Error("Новый пароль не должен совпадать с текущим"));
                },
              }),
            ]}
          >
            <Input.Password
              placeholder="Придумайте новый пароль"
              autoComplete="new-password"
              onPressEnter={() => onSave()}
            />
          </Form.Item>
        </Form>
      </Drawer>
    </Header>
  );
}
